import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_ESCAPE_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { $findMatchingParent, mergeRegister } from '@lexical/utils';
import { $isAutoLinkNode, $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $isAtNodeEnd } from '@lexical/selection';

import { toolbarItemClass } from './ToolbarPlugin';
import PencilIcon from '../icons/PencilIcon';
import TrashIcon from '../icons/TrashIcon';
import XIcon from '../icons/XIcon';

import { setFloatingElemPositionForLinkEditor } from '../utils/setFloatingElemPositionForLinkEditor';
import { sanitizeUrl } from '../utils/url';

function getSelectedNode(selection) {
  const anchor = selection.anchor;
  const focus = selection.focus;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
  }
}

// A collapsed caret at either edge of a link is outside it: LinkNode refuses
// text inserted before or after.
function $isCaretAtLinkEdge(selection, linkNode) {
  if (!selection.isCollapsed()) return false;
  const { anchor } = selection;
  if (anchor.type !== 'text') return false;
  const textNode = anchor.getNode();
  if (anchor.offset === 0) return textNode.is(linkNode.getFirstDescendant());
  return (
    anchor.offset === textNode.getTextContentSize() && textNode.is(linkNode.getLastDescendant())
  );
}

export function $getSelectedLinkNode() {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return null;
  const node = getSelectedNode(selection);
  const linkParent = $findMatchingParent(node, $isLinkNode);
  if (linkParent === null) return null;
  if ($findMatchingParent(node, $isAutoLinkNode) !== null) return null;
  if ($isCaretAtLinkEdge(selection, linkParent)) return null;
  return linkParent;
}

function FloatingLinkEditor({
  editor,
  isLink,
  setIsLink,
  anchorElem,
  setIsLinkEditMode,
  isLinkEditMode,
}) {
  const editorRef = useRef(null);
  const inputRef = useRef(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [editedLinkUrl, setEditedLinkUrl] = useState('https://');
  const [lastSelection, setLastSelection] = useState(null);

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);

      if (linkParent) {
        setLinkUrl(linkParent.getURL());
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL());
      } else {
        setLinkUrl('');
      }

      if (isLinkEditMode) {
        setEditedLinkUrl(linkUrl);
      }
    }

    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      const domRect = nativeSelection.focusNode?.parentElement?.getBoundingClientRect();

      if (domRect) {
        domRect.y += 40;
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem);
      }

      setLastSelection(selection);
    } else if (!activeElement || activeElement.className !== 'link-input') {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem);
      }

      setLastSelection(null);
      setIsLinkEditMode(false);
      setLinkUrl('');
    }

    return true;
  }, [anchorElem, editor, setIsLinkEditMode, isLinkEditMode, linkUrl]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        updateLinkEditor();
      });
    };

    window.addEventListener('resize', update);

    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update);
    }

    return () => {
      window.removeEventListener('resize', update);

      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update);
      }
    };
  }, [anchorElem.parentElement, editor, updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [editor, updateLinkEditor, setIsLink, isLink]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor, isLink]);

  useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLinkEditMode, isLink]);

  const saveLink = () => {
    handleLinkSubmission();
    setLastSelection(null);
    setIsLinkEditMode(false);
  };

  const monitorInputInteraction = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveLink();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };

  const handleLinkSubmission = () => {
    if (lastSelection !== null) {
      if (linkUrl !== '') {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(editedLinkUrl));
      }

      setEditedLinkUrl('https://');
      setIsLinkEditMode(false);
    }
  };

  if (!isLink) return null;

  return (
    <div
      ref={editorRef}
      className="absolute top-0 left-0 z-10 max-w-[18rem] w-full opacity-0 bg-white dark:bg-popover rounded transition-opacity transform h-10 shadow-input"
    >
      {isLinkEditMode ? (
        <div className="flex flex-row items-center gap-x-1 p-1 h-full">
          <input
            className="link-input min-w-0 flex-1 h-4 py-0 leading-4 px-2 text-xs bg-transparent border-0 shadow-none outline-none ring-0 focus:ring-0 focus:outline-none"
            type="text"
            ref={inputRef}
            value={editedLinkUrl}
            onChange={event => {
              setEditedLinkUrl(event.target.value);
            }}
            onKeyDown={event => {
              monitorInputInteraction(event);
            }}
          />

          <button
            type="button"
            aria-label="Save"
            className={`${toolbarItemClass} shrink-0 text-xs font-medium`}
            onMouseDown={event => event.preventDefault()}
            onClick={() => saveLink()}
          >
            Save
          </button>

          <button
            type="button"
            aria-label="Cancel"
            className={`${toolbarItemClass} shrink-0`}
            onMouseDown={event => event.preventDefault()}
            onClick={() => {
              setIsLinkEditMode(false);
            }}
          >
            <XIcon />
          </button>
        </div>
      ) : (
        <div className="flex flex-row items-center p-1 h-full">
          <div className="p-2 min-w-0">
            <a
              className="block truncate text-xs"
              href={sanitizeUrl(linkUrl)}
              title={linkUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {linkUrl}
            </a>
          </div>

          <button
            className="flex-1 h-full"
            onClick={() => {
              setEditedLinkUrl(linkUrl);
              setIsLinkEditMode(true);
            }}
          ></button>

          <button
            type="button"
            aria-label="Edit link"
            className={`${toolbarItemClass} shrink-0 size-7 items-center justify-center`}
            onMouseDown={event => event.preventDefault()}
            onClick={() => {
              setEditedLinkUrl(linkUrl);
              setIsLinkEditMode(true);
            }}
          >
            <PencilIcon size={13} />
          </button>

          <button
            type="button"
            aria-label="Remove link"
            className={`${toolbarItemClass} shrink-0 size-7 items-center justify-center`}
            onMouseDown={event => event.preventDefault()}
            onClick={() => {
              editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
            }}
          >
            <TrashIcon size={14} />
          </button>
        </div>
      )}
    </div>
  );
}

function useFloatingLinkEditorToolbar(editor, anchorElem, isLinkEditMode, setIsLinkEditMode) {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);

  useEffect(() => {
    function updateToolbar() {
      if (!$isRangeSelection($getSelection())) return;
      setIsLink($getSelectedLinkNode() !== null);
    }

    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL,
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        payload => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const linkNode = $findMatchingParent(node, $isLinkNode);
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), '_blank');
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor]);

  return createPortal(
    <FloatingLinkEditor
      editor={activeEditor}
      isLink={isLink}
      anchorElem={anchorElem}
      setIsLink={setIsLink}
      isLinkEditMode={isLinkEditMode}
      setIsLinkEditMode={setIsLinkEditMode}
    />,
    anchorElem,
  );
}

export default function FloatingLinkEditorPlugin({
  anchorElem = document.body,
  isLinkEditMode,
  setIsLinkEditMode,
}) {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(editor, anchorElem, isLinkEditMode, setIsLinkEditMode);
}
